import React from 'react';

import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';

const FormTagsPicker = ({ options, formik, id, label }) => {
  const { values, setFieldValue } = formik;
  const [inputValue, setInputValue] = React.useState('');
  const isAddingTagRef = React.useRef(false);
  const previousTagsLengthRef = React.useRef((values[id] || []).length);

  // Clear input when tag is added
  React.useEffect(() => {
    const currentTagsLength = (values[id] || []).length;
    if (currentTagsLength > previousTagsLengthRef.current && isAddingTagRef.current) {
      setInputValue('');
      isAddingTagRef.current = false;
    }
    previousTagsLengthRef.current = currentTagsLength;
  }, [values, id]);

  const onChange = (event, newValues) => {
    const valuesToSet = newValues.map(value => {
      const { inputValue: valInputValue } = value;
      if (valInputValue) {
        const modifiedValue = value;
        modifiedValue.label = valInputValue;
        return modifiedValue;
      }
      return value;
    });
    setFieldValue(id, valuesToSet);
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter' && inputValue.trim() !== '') {
      event.preventDefault();
      event.stopPropagation();
      const currentValues = values[id] || [];
      const trimmedInput = inputValue.trim();

      // Check if tag already exists
      const tagExists = currentValues.some(tag => (typeof tag === 'string' ? tag : tag.key || tag.label) === trimmedInput);

      if (!tagExists) {
        isAddingTagRef.current = true;
        const newTag = {
          key: trimmedInput,
          label: trimmedInput,
        };
        setFieldValue(id, [...currentValues, newTag]);
        // Don't set inputValue here - useEffect will handle it
      }
    }
  };

  return (
    <Autocomplete
      data-testid={`${id}-tags-picker`}
      filterOptions={(opts, { inputValue: filterInputValue }) => {
        const filtered = opts.filter(option => option.label.toLowerCase().includes(filterInputValue.toLowerCase()));
        if (filterInputValue !== '' && !filtered.some(option => option.label.toLowerCase() === filterInputValue.toLowerCase())) {
          filtered.push({
            key: filterInputValue,
            label: `Add "${filterInputValue}"`,
            inputValue: filterInputValue,
          });
        }

        return filtered;
      }}
      freeSolo
      getOptionLabel={option => option.label}
      inputValue={inputValue}
      limitTags={4}
      multiple
      onChange={onChange}
      onInputChange={(event, newInputValue, reason) => {
        // Don't update inputValue if we're adding a tag via Enter
        if (isAddingTagRef.current && reason !== 'reset') {
          return;
        }
        setInputValue(newInputValue);
      }}
      options={options}
      renderInput={params => (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            value: inputValue,
          }}
          label={label}
          onKeyDown={handleKeyDown}
          variant='outlined'
        />
      )}
      renderOption={option => option.label}
      value={values[id] || []}
    />
  );
};

FormTagsPicker.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  formik: PropTypes.shape({
    setFieldValue: PropTypes.func,
    values: PropTypes.shape({}),
  }).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
};

FormTagsPicker.defaultProps = {
  options: [],
};

export default FormTagsPicker;
